import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  rateMatrixBodyRecipe,
  rateMatrixCellRecipe,
  rateMatrixRecipe,
  rateMatrixRowRecipe,
  type RateMatrixCellRecipeProps,
  type RateMatrixRowRecipeProps,
} from '../recipes/rate-matrix.recipe';

/**
 * How many columns each row draws. Set once on the matrix so a row cannot
 * disagree with the header above it.
 */
const ColumnsContext = React.createContext<number>(1);

export interface RateMatrixProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Total columns, the label column included. */
  columns: number;
}

/**
 * The editable pricing grid: a service down the left, a vehicle class across
 * the top, a rate in every cell.
 *
 *   <RateMatrix columns={1 + classes.length}>
 *     <RateMatrixRow header>
 *       <RateMatrixCell kind="corner">Rate Name</RateMatrixCell>
 *       {classes.map((c) => (
 *         <RateMatrixCell key={c.key} kind="heading" tone={c.tone}>{c.label}</RateMatrixCell>
 *       ))}
 *     </RateMatrixRow>
 *     {services.map((s) => (
 *       <RateMatrixRow key={s.id}>
 *         <RateMatrixCell kind="label">{s.name}</RateMatrixCell>
 *         {classes.map((c) => (
 *           <RateMatrixCell key={c.key}><Input … /></RateMatrixCell>
 *         ))}
 *       </RateMatrixRow>
 *     ))}
 *   </RateMatrix>
 *
 * The layout only. Which cell is selected, what an edit does and how a value
 * is formatted all stay with the page; they arrive as `active` and as the
 * Input each value cell holds. The frame scrolls both ways and the header row
 * stays pinned, which is the part a Table could not do with an Input in every
 * cell.
 */
export const RateMatrix = React.forwardRef<HTMLDivElement, RateMatrixProps>(
  function RateMatrix({ columns, className, children, ...props }, ref) {
    return (
      <ColumnsContext.Provider value={columns}>
        <div ref={ref} className={cn(rateMatrixRecipe(), className)} {...props}>
          <div className={rateMatrixBodyRecipe()}>{children}</div>
        </div>
      </ColumnsContext.Provider>
    );
  }
);

export interface RateMatrixRowProps
  extends React.HTMLAttributes<HTMLDivElement>,
    RateMatrixRowRecipeProps {}

/** One row of the matrix. `header` pins it to the top of the scrolling frame. */
export const RateMatrixRow = React.forwardRef<
  HTMLDivElement,
  RateMatrixRowProps
>(function RateMatrixRow({ header, className, style, ...props }, ref) {
  const columns = React.useContext(ColumnsContext);
  return (
    <div
      ref={ref}
      className={cn(rateMatrixRowRecipe({ header }), className)}
      // Written as a style rather than a class: Tailwind only generates the
      // column counts it can read from source, and this one is data.
      style={{ gridTemplateColumns: `repeat(${columns}, 1fr)`, ...style }}
      {...props}
    />
  );
});

export interface RateMatrixCellProps
  extends React.HTMLAttributes<HTMLDivElement>,
    RateMatrixCellRecipeProps {}

/**
 * One cell. `kind` says which of the four it is; `tone` tints a heading by
 * vehicle class; `active` is the highlight that follows the selected cell
 * along its row and column; `adjusted` marks a value cell whose rate has
 * been overridden for this account, and the `<Input variant="cell">` inside
 * takes that colour.
 */
export const RateMatrixCell = React.forwardRef<
  HTMLDivElement,
  RateMatrixCellProps
>(function RateMatrixCell(
  { kind, tone, active, adjusted, className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(
        rateMatrixCellRecipe({ kind, tone, active, adjusted }),
        className
      )}
      {...props}
    />
  );
});
